import { createApi, retry } from '@reduxjs/toolkit/query/react';
import { restEndpointBaseQuery } from '../../providers/AppStateProvider/restEndpointBaseQuery';

/**
 * Centralized list of cache tag types for RTK Query.
 * Add new tag types here to enable cache invalidation across the application.
 */
export const TAG_TYPES = [
  'UserApiKey',
  'MarketAccount',
  'MarketAccountDetails',
  'LayoutSpec',
  'AdjustedSettlementDate',
  'PreTrade',
  'EffectivePricingRule',
  'MarginCost',
  'AssetTransaction',
] as const;

export type TagType = (typeof TAG_TYPES)[number];

/**
 * Creates a typed cache tag for RTK Query.
 * Use this in `providesTags` and `invalidatesTags` to ensure type safety.
 *
 * @example
 * ```ts
 * providesTags: (_result, _error, { userID }) => [createTag('UserApiKey', userID)]
 * invalidatesTags: (_result, _error, { userID }) => [createTag('UserApiKey', userID)]
 * ```
 */
export function createTag<T extends TagType>(type: T, id: string) {
  return { type, id };
}

/**
 * Base RTK Query API instance.
 *
 * Use `baseRestApi.injectEndpoints()` to add new endpoints.
 *
 * **IMPORTANT: This should be the ONLY API slice in the application except for another for streaming.**
 *
 * Do NOT create separate `createApi()` instances. Instead, inject endpoints into this base API.
 * Everything except `reducerPath` and `tagTypes` can be overridden by consumers.
 *
 * @example
 * ```ts
 * const myEndpoints = baseRestApi.injectEndpoints({
 *   endpoints: builder => ({
 *     getData: builder.query<Response, { id: string }>({
 *       query: ({ id }) => ({ method: 'GET', url: `/data/${id}` }),
 *       providesTags: (_result, _error, { id }) => [createTag('UserApiKey', id)],
 *     }),
 *   }),
 * });
 * ```
 */
const baseRestApiInternal = createApi({
  reducerPath: 'baseRestApi',
  // wrap with a retry to allow injected endpoints to override per-endpoint retries. See: https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#automatic-retries
  baseQuery: retry(restEndpointBaseQuery, { maxRetries: 0 }), // no retries per default, each endpoint needs to set it to non-zero explicitly
  endpoints: () => ({}), // Endpoints are injected by consumers
  tagTypes: TAG_TYPES,
});

// Wrap the injectEndpoint function to default overrideExisting to throw. If we have colliding endpoints names at runtime, this will cause an exception to be thrown.
// Can be overridden per endpoint
const injectEndpoints: typeof baseRestApiInternal.injectEndpoints = (...args) => {
  const [params] = args;
  return baseRestApiInternal.injectEndpoints({
    overrideExisting: 'throw',
    ...params,
  });
};

export const baseRestApi = {
  util: baseRestApiInternal.util,
  injectEndpoints,
  reducer: baseRestApiInternal.reducer,
  middleware: baseRestApiInternal.middleware,
};
